import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { ButtonGroup, Modal } from 'slab'
import { usePutQuotingProspectsId } from 'utils/web_api'
import { type PutQuotingProspectsId, PutQuotingProspectsIdReasonsForChangingInsuranceItem } from 'utils/web_api.schemas'

import { BusinessDetailsForm, type BusinessFormData } from '../BusinessDetailsForm'
import { useProspect } from '../ProspectProvider'

export function SummaryEditPage () {
  const { prospect, refetch } = useProspect()
  const [isDiscardConfirmationOpen, setDiscardConfirmationOpen] = useState(false)
  const navigate = useNavigate()

  const { mutate: updateProspect } = usePutQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        refetch()
        toast.success('Changes saved!')
        navigateBack()
      }
    }
  })

  const form = useForm<BusinessFormData>({
    defaultValues: {
      businessName: prospect?.name,
      employeeCount: prospect?.employee_count ?? 0,
      offersBenefits: prospect?.offers_health_insurance_today,
      coverageStartDate: prospect?.health_insurance_effective_date ?? '',
      reasons: {
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits) ?? false,
        [PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs]: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs) ?? false,
        other: !!prospect?.other_reason_for_changing_insurance
      },
      otherReason: prospect?.other_reason_for_changing_insurance ?? ''
    },
    mode: 'onChange'
  })

  const { formState: { isDirty } } = form

  const navigateBack = () => navigate('..')

  const onBack = () => {
    if (isDirty) {
      setDiscardConfirmationOpen(true)
    } else {
      navigateBack()
    }
  }

  const onSubmit: SubmitHandler<BusinessFormData> = (data) => {
    if (!prospect) {
      return
    }

    const prospectData: Partial<PutQuotingProspectsId> = {
      name: data.businessName,
      employee_count: data.employeeCount,
      offers_health_insurance_today: data.offersBenefits,
      health_insurance_effective_date: data.coverageStartDate,
      reasons_for_changing_insurance: Object.keys(data.reasons)
        .filter(key => key !== 'other')
        .map(key => key as PutQuotingProspectsIdReasonsForChangingInsuranceItem)
        .filter(key => data.reasons[key]),
      other_reason_for_changing_insurance: data.otherReason
    }

    updateProspect({
      id: prospect.id,
      data: prospectData
    })
  }

  return (
    <>
      <BusinessDetailsForm
        form={form}
        title='Edit business details'
        backText='Discard changes'
        submitText='Save changes'
        onBack={onBack}
        onSubmit={onSubmit}
      />
      <Modal
        open={isDiscardConfirmationOpen}
        onOpenChange={setDiscardConfirmationOpen}
        zIndex={1}
      >
        <Modal.Body>
          <Modal.Title>
            Discard changes?
          </Modal.Title>
          <Modal.Description>
            Any changes will not be saved
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Modal.Button
              variant='secondary'
              size='large'
              onClick={() => setDiscardConfirmationOpen(false)}
            >
              Cancel
            </Modal.Button>
            <Modal.Button
              variant='tertiary-destructive'
              size='large'
              onClick={navigateBack}
            >
              Discard
            </Modal.Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  )
}
